import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CalendarClock, Plus, Check, UserX, Loader2, X, Clock, MapPin,
} from 'lucide-react'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import { appointments, members, staff } from '../api/endpoints.js'

const STATUSES = ['', 'scheduled', 'completed', 'no_show', 'cancelled']

const fmtTime = (d) => {
  if (!d) return '—'
  try { return format(new Date(d), 'h:mm a') } catch { return d }
}
const fmtDay = (d) => {
  if (!d) return '—'
  try { return format(new Date(d), 'EEE MMM d') } catch { return d }
}
const fmtMoney = (v) => (v != null ? `$${parseFloat(v).toFixed(2)}` : '—')

function StatusBadge({ status }) {
  const cls =
    status === 'completed' ? 'badge-active'
    : status === 'no_show' ? 'badge-suspended'
    : status === 'cancelled' ? 'badge-cancelled'
    : 'badge-alert'
  return <span className={cls}>{(status ?? 'scheduled').replace('_', ' ')}</span>
}

function NewAppointmentDialog({ open, onClose, onCreated }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    member_id: '', staff_id: '', type: 'personal_training',
    start_time: '', duration_minutes: 60, price: 0, location: '', notes: '',
  })

  const { data: memberData } = useQuery({
    queryKey: ['members', 'picker'],
    queryFn: () => members.list({ page: 1, per_page: 500 }).then((r) => r.data),
    enabled: open,
  })
  const { data: staffData } = useQuery({
    queryKey: ['staff', 'picker'],
    queryFn: () => staff.list().then((r) => r.data),
    enabled: open,
  })

  const memberList = Array.isArray(memberData) ? memberData : memberData?.items ?? []
  const staffList = Array.isArray(staffData) ? staffData : staffData?.items ?? []

  const create = useMutation({
    mutationFn: (payload) => appointments.create(payload),
    onSuccess: () => {
      toast.success('Appointment booked')
      qc.invalidateQueries({ queryKey: ['appointments'] })
      onCreated?.()
      onClose()
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not book appointment'),
  })

  if (!open) return null

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const submit = () => {
    if (!form.member_id || !form.staff_id || !form.start_time) {
      toast.error('Member, trainer and start time are required')
      return
    }
    const start = new Date(form.start_time)
    const end = new Date(start.getTime() + Number(form.duration_minutes || 60) * 60000)
    create.mutate({
      member_id: form.member_id,
      staff_id: form.staff_id,
      type: form.type,
      start_time: start.toISOString(),
      end_time: end.toISOString(),
      duration_minutes: Number(form.duration_minutes || 60),
      price: Number(form.price || 0),
      location: form.location || null,
      notes: form.notes || null,
      is_recurring: false,
    })
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/55 backdrop-blur-sm animate-fade-in p-4"
         onClick={onClose}>
      <div className="card w-full max-w-lg animate-pop max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-bold">New appointment</h3>
          <button onClick={onClose} className="btn-ghost p-1.5" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="label" htmlFor="ap-member">Member</label>
            <select id="ap-member" className="input-field" value={form.member_id} onChange={set('member_id')}>
              <option value="">Select a member…</option>
              {memberList.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.full_name || `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() || m.email || m.id}
                </option>
              ))}
            </select>
          </div>

          <div className="col-span-2">
            <label className="label" htmlFor="ap-staff">Trainer</label>
            <select id="ap-staff" className="input-field" value={form.staff_id} onChange={set('staff_id')}>
              <option value="">Select a trainer…</option>
              {staffList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.full_name || `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim() || s.email}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label" htmlFor="ap-type">Type</label>
            <select id="ap-type" className="input-field" value={form.type} onChange={set('type')}>
              <option value="personal_training">Personal training</option>
              <option value="semi_private">Semi-private</option>
              <option value="consultation">Consultation</option>
              <option value="assessment">Assessment</option>
              <option value="recovery">Recovery</option>
            </select>
          </div>

          <div>
            <label className="label" htmlFor="ap-dur">Duration (min)</label>
            <input id="ap-dur" type="number" min="15" step="15" className="input-field"
                   value={form.duration_minutes} onChange={set('duration_minutes')} />
          </div>

          <div>
            <label className="label" htmlFor="ap-start">Start</label>
            <input id="ap-start" type="datetime-local" className="input-field"
                   value={form.start_time} onChange={set('start_time')} />
          </div>

          <div>
            <label className="label" htmlFor="ap-price">Price</label>
            <input id="ap-price" type="number" min="0" step="0.01" className="input-field"
                   value={form.price} onChange={set('price')} />
          </div>

          <div className="col-span-2">
            <label className="label" htmlFor="ap-loc">Location</label>
            <input id="ap-loc" className="input-field" placeholder="Main floor, Studio B…"
                   value={form.location} onChange={set('location')} />
          </div>

          <div className="col-span-2">
            <label className="label" htmlFor="ap-notes">Notes</label>
            <input id="ap-notes" className="input-field" value={form.notes} onChange={set('notes')} />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={create.isPending} onClick={submit}>
            {create.isPending ? 'Booking…' : 'Book appointment'}
          </button>
        </div>
      </div>
    </div>
  )
}

function RescheduleDialog({ appt, onClose }) {
  const qc = useQueryClient()
  const start = appt.start_time ? new Date(appt.start_time) : new Date()
  const pad = (n) => String(n).padStart(2, '0')
  const [date, setDate] = useState(
    `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`)
  const [time, setTime] = useState(`${pad(start.getHours())}:${pad(start.getMinutes())}`)
  const [dur, setDur] = useState(appt.duration_minutes ?? 60)

  const save = useMutation({
    mutationFn: () => {
      const s = new Date(`${date}T${time}`)
      const e = new Date(s.getTime() + Number(dur) * 60000)
      return appointments.update(appt.id, {
        start_time: s.toISOString(),
        end_time: e.toISOString(),
        duration_minutes: Number(dur),
      })
    },
    onSuccess: () => {
      toast.success('Appointment rescheduled')
      qc.invalidateQueries({ queryKey: ['appointments'] })
      onClose()
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not reschedule'),
  })

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm animate-fade-in p-4"
         onClick={onClose}>
      <div className="card w-full max-w-sm animate-pop" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-bold">Reschedule</h3>
          <button onClick={onClose} className="btn-ghost p-1.5" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-text-muted mb-3">{appt.member_name ?? appt.member_id}</p>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label">Date</label>
            <input type="date" className="input-field" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div><label className="label">Start</label>
            <input type="time" className="input-field" value={time} onChange={(e) => setTime(e.target.value)} /></div>
          <div className="col-span-2"><label className="label">Duration (min)</label>
            <input type="number" min="15" step="15" className="input-field" value={dur}
                   onChange={(e) => setDur(e.target.value)} /></div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Reschedule'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function AppointmentsPage() {
  const qc = useQueryClient()
  const [date, setDate] = useState('')
  const [status, setStatus] = useState('')
  const [dialog, setDialog] = useState(false)
  const [reschedule, setReschedule] = useState(null)
  const [busyId, setBusyId] = useState(null)

  const { data, isLoading } = useQuery({
    queryKey: ['appointments', { date, status }],
    queryFn: () => appointments.list({
      ...(date ? { date } : {}),
      ...(status ? { status } : {}),
    }).then((r) => r.data),
  })

  const list = Array.isArray(data) ? data : data?.items ?? []

  const act = useMutation({
    mutationFn: ({ kind, id }) =>
      kind === 'complete' ? appointments.complete(id) : appointments.noShow(id),
    onMutate: ({ id }) => setBusyId(id),
    onSuccess: (_r, { kind }) => {
      toast.success(kind === 'complete' ? 'Marked completed' : 'Marked no-show')
      qc.invalidateQueries({ queryKey: ['appointments'] })
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Action failed'),
    onSettled: () => setBusyId(null),
  })

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="page-title">Appointments</h1>
        <button className="btn-primary inline-flex items-center gap-2" onClick={() => setDialog(true)}>
          <Plus className="w-4 h-4" /> New appointment
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label" htmlFor="f-date">Date</label>
          <input id="f-date" type="date" className="input-field w-auto"
                 value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="f-status">Status</label>
          <select id="f-status" className="input-field w-auto"
                  value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s === '' ? 'All statuses' : s.replace('_', ' ')}</option>
            ))}
          </select>
        </div>
        {(date || status) && (
          <button className="btn-ghost" onClick={() => { setDate(''); setStatus('') }}>Clear</button>
        )}
      </div>

      <div className="table-container">
        <table className="table-base">
          <thead className="table-head">
            <tr>
              <th>When</th><th>Member</th><th>Trainer</th><th>Type</th>
              <th>Location</th><th>Price</th><th>Status</th><th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-t border-surface-3">
                  {Array.from({ length: 8 }).map((_, j) => (
                    <td key={j} className="px-4 py-3"><div className="skeleton h-4 w-3/4" /></td>
                  ))}
                </tr>
              ))
            ) : list.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-14 text-center text-text-dim">
                  <CalendarClock className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  No appointments{date || status ? ' match these filters' : ' booked yet'}.
                </td>
              </tr>
            ) : (
              list.map((a, i) => {
                const busy = busyId === a.id
                const done = a.status === 'completed' || a.status === 'no_show' || a.status === 'cancelled'
                return (
                  <tr key={a.id ?? i} className="border-t border-surface-3 hover:bg-surface-3/70 transition-colors">
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-text-primary">{fmtDay(a.start_time)}</div>
                      <div className="text-text-dim inline-flex items-center gap-1 text-xs">
                        <Clock className="w-3 h-3" />{fmtTime(a.start_time)}
                        {a.duration_minutes ? ` · ${a.duration_minutes}m` : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-primary">{a.member_name ?? a.member_id ?? '—'}</td>
                    <td className="px-4 py-3 text-sm text-text-muted">{a.staff_name ?? a.staff_id ?? '—'}</td>
                    <td className="px-4 py-3 text-sm text-text-muted">{(a.type ?? '').replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3 text-sm text-text-muted">
                      {a.location
                        ? <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{a.location}</span>
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm tabular-nums text-text-primary">{fmtMoney(a.price)}</td>
                    <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-0.5">
                        {!done && (
                          <>
                            <button
                              title="Reschedule" aria-label="Reschedule" disabled={busy}
                              onClick={() => setReschedule(a)}
                              className="p-1.5 rounded-md text-text-dim hover:text-primary hover:bg-primary/12 transition-all duration-150 active:scale-95 disabled:opacity-40"
                            >
                              <CalendarClock className="w-4 h-4" />
                            </button>
                            <button
                              title="Mark completed" aria-label="Mark completed" disabled={busy}
                              onClick={() => act.mutate({ kind: 'complete', id: a.id })}
                              className="p-1.5 rounded-md text-text-dim hover:text-[rgb(var(--ok))] hover:bg-[rgb(var(--ok))]/12 transition-all duration-150 active:scale-95 disabled:opacity-40"
                            >
                              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            </button>
                            <button
                              title="Mark no-show" aria-label="Mark no-show" disabled={busy}
                              onClick={() => act.mutate({ kind: 'noShow', id: a.id })}
                              className="p-1.5 rounded-md text-text-dim hover:text-[rgb(var(--danger))] hover:bg-[rgb(var(--danger))]/12 transition-all duration-150 active:scale-95 disabled:opacity-40"
                            >
                              <UserX className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <NewAppointmentDialog open={dialog} onClose={() => setDialog(false)} />
      {reschedule && (
        <RescheduleDialog appt={reschedule} onClose={() => setReschedule(null)} />
      )}
    </div>
  )
}
